<template>
    <div v-if="isVisible" class="user-invoices-box box">
        <div class="box-title">
            Invoices
        </div>
        <div class="box-content">
            <transition
            name="fade"
            mode="out-in"
            >
                <div
                    v-if="isLoading"
                    class="loader"
                >
                    <i class="fas fa-spinner fa-spin loader" />
                </div>
                <template v-else>
                    <ul class="invoices-listing">
                        <template v-for="(invoice, index) in invoices">
                            <li v-if="! isFreePlanInvoice(invoice)" :key="index">
                                <div class="invoices-header">
                                    <div class="invoices-date">
                                        Date
                                    </div>
                                    <div class="invoices-amount">
                                        Amount
                                    </div>
                                    <div class="invoices-number">
                                        # of Shows
                                    </div>
                                    <div class="invoices-number">
                                        # of Hostings
                                    </div>
                                </div>
                                <div class="invoices-details">
                                    <div class="invoices-date">
                                        {{ dateExplicit( invoice.created ) }}
                                    </div>
                                    <div class="invoices-amount">
                                        {{ formatPrice( invoice.total ) }}
                                    </div>
                                    <div class="invoices-number">
                                        {{ invoiceShows( invoice ) }}
                                    </div>
                                    <div class="invoices-number">
                                        {{ invoiceHostings( invoice ) }}
                                    </div>
                                    <div class="invoices-download">
                                        <a class="functional-button" :href="`/api/billing/invoices/${invoice.id}/download`">
                                            <i class="fas fa-file-pdf" />
                                            <span>View invoice</span>
                                        </a>
                                    </div>
                                </div>
                            </li>
                        </template>
                    </ul>
                </template>
        </transition>
        </div>
    </div>
</template>

<script>
import { chain, flatMap, reduce } from 'lodash';
import { mapState, mapActions } from 'vuex';
import usdFormatter from '@/utils/usdFormatter';
import moment from 'moment';
import toasts from '@/mixins/toasts';

export default {
    mixins: [toasts],

    data: () => ({
        freePlan: 'plan_H4cQhLOO9JbveX',
    }),

    computed: {
        ...mapState({
            invoices: state => state.invoices,
            products: state => state.products,
            billingPlans: state => state.billingPlans,
            isVisible: state => state.invoices && 0 < state.invoices.length,
            isLoading: state => null === state.invoices ||
                null === state.products ||
                null === state.billingPlans,
            currentSubscription: state => state.currentSubscription,
        }),

        /**
         * Collects every plan-tier Stripe price ID across all plans.
         *
         * Each plan exposes its prices keyed by interval (`month`/`year`) with
         * the Stripe price object as the value, so a show billed on any tier
         * (Starter, Pro, legacy) is recognised — not only the legacy "Fusebox
         * Pro Player" price exposed by /api/billing/products.
         *
         * @returns {string[]} Plan price IDs.
         */
        planPriceIds() {
            return flatMap(
                this.billingPlans || [],
                plan => Object.values(plan.prices || {}).map(price => price.id)
            );
        },
    },

    watch: {
        currentSubscription: {
            immediate: true,

            /**
             * Watch changes on subscription
             *
             * @param {object} newSubscription New loaded subscription.
             * @param {object} oldSubscription Old loaded subscription.
             */
            handler(newSubscription, oldSubscription) {
                if (oldSubscription?.stripe_id &&
                    newSubscription?.stripe_id &&
                    newSubscription.stripe_id !== oldSubscription.stripe_id
                ) {
                    this.loadUpcomingInvoice();
                    this.loadInvoices({ force: true });
                }
            },
        },
    },

    /**
     * Loads required data.
     */
    mounted() {
        this.$store.dispatch('loadProducts');
        this.$store.dispatch('loadPlans');
    },

    /**
     * Updated hook
     */
    updated() {
        if (false === this.invoices) {
            this.toastError({
                message: {
                    title: 'We could not import your invoices. Please try again later',
                },
            });
        }
    },

    methods: {
        ...mapActions([
            'loadInvoices',
            'loadUpcomingInvoice',
        ]),

        /**
         * Determines whether the invoice is a free-plan invoice.
         *
         * Reads the first line item defensively: Stripe's 2022-11-15 line
         * items expose `price` instead of the legacy `plan`, so `plan` can be
         * null (e.g. on invoices created by a plan swap). The previous
         * unguarded `plan.id` access threw during render and blanked the whole
         * invoices list.
         *
         * @param   {object}  invoice Invoice data.
         * @returns {boolean}         Whether the invoice is for the free plan.
         */
        isFreePlanInvoice(invoice) {
            return this.freePlan === invoice.lines?.data?.[0]?.plan?.id;
        },

        /**
         * Formats explicit date
         *
         * @param   { number }  date  Date timestamp
         * @returns  { string }  Formated date.
         */
        dateExplicit(date) {
            return moment.unix(date).format('MMM D, YYYY');
        },

        /**
         * Format price in USD
         *
         * @param  {number} price Price in cents.
         * @returns {string}       Formatted price.
         */
        formatPrice(price) {
            return usdFormatter(price);
        },

        /**
         * Counts shows in invoice.
         *
         * @param   {object} invoice Invoice data.
         * @returns {number}         Count of shows in invoice.
         */
        invoiceShows(invoice) {
            return this.countInvoiceItems(invoice, this.planPriceIds);
        },

        /**
         * Counts hostings in invoice.
         *
         * @param   {object} invoice Invoice data.
         * @returns {number}         Count of hostings in invoice.
         */
        invoiceHostings(invoice) {
            const prices = Object.values(
                chain(this.products).find({ type: 'addon', 'add-on': 'hosting' }).get('prices').value() || {}
            );

            return this.countInvoiceItems(invoice, prices);
        },

        /**
         * Counts invoice items matching given products.
         *
         * @param   {object}   invoice Invoice data.
         * @param   {string[]} prices  List of product prices.
         * @returns {number}           Count of items.
         */
        countInvoiceItems(invoice, prices) {
            return reduce(invoice.lines.data, (count, lineItem) => {
                // do not count negative items
                if (0 >= lineItem.amount) {
                    return count;
                }

                const priceId = lineItem.price?.id;

                if (! priceId || ! prices.includes(priceId)) {
                    return count;
                }

                return count + lineItem.quantity;
            }, 0);
        },
    },
};
</script>
